import { DocType, Document } from "models/documents"
import {
  Supplier,
  SupplierDetails,
  SupplierOptionTuple,
  SupplierProduct,
  SupplierProductOptionTuple,
} from "models/supplier"

import { Paginated } from "./types"
import { http } from "./http"

export const fetchSuppliers = async (_key: string, params?: string) => {
  const searchParams = new URLSearchParams(params)
  const res = await http.get("suppliers", { searchParams }).json()
  return res as Paginated<Supplier>
}

export const fetchPlanSuppliers = async (
  _key: string,
  planId: string,
  params?: string,
) => {
  const searchParams = new URLSearchParams(params)
  const res = await http
    .get(`media-plans/${planId}/suppliers`, { searchParams })
    .json()
  return res as Paginated<SupplierOptionTuple>
}

export const fetchPlanCrSuppliers = async (
  _key: string,
  planId: string,
  vendorProductId?: string,
  params?: string,
) => {
  const searchParams = new URLSearchParams(params)
  if (!vendorProductId) Promise.reject()
  const { data: res } = (await http
    .get(`media-plans/${planId}/channels/${vendorProductId}/suppliers`, {
      searchParams,
    })
    .json()) as { data: Array<SupplierOptionTuple> }
  return res
}

export const fetchSupplier = async (_key: string, id: string) => {
  const res = await http.get(`suppliers/${id}`).json()
  return res as SupplierDetails
}

export type PatchSupplierPayload = {
  id: string
  data: Partial<SupplierDetails>
}
export const patchSupplier = async (payload: PatchSupplierPayload) => {
  const { id, data: json } = payload
  const res = await http.patch(`suppliers/${id}`, { json }).json()

  return res as SupplierDetails
}

export const activateSupplier = async (id: string) =>
  await http.patch(`suppliers/${id}/activate`)

export const deactivateSupplier = async (id: string) =>
  await http.patch(`suppliers/${id}/deactivate`)

export const fetchSupplierDocuments = async (_key: string, id: string) => {
  const res = await http(
    `suppliers/${id}/documents?sort=DATE_CREATED,DESC`,
  ).json()
  return res as Array<Document>
}

type UploadSupplierDocPld = {
  file?: File
  mediaType?: string
  description?: string
  ownerEntityId: string
  documentType?: DocType
}

export const uploadSupplierDocument = async (pld: UploadSupplierDocPld) => {
  if (pld.file === undefined) return
  const { file, ownerEntityId, documentType, description } = pld
  const formData = new FormData()
  formData.append("file", file)
  formData.append("name", file.name)
  if (documentType !== undefined) {
    formData.append("documentType", documentType)
  }
  if (description !== undefined) {
    formData.append("description", description)
  }

  const res = await http
    .post(`suppliers/${ownerEntityId}/documents`, {
      body: formData,
      timeout: false,
    })
    .json()
  return res
}

export const deleteSupplierDocument = async (id: string) => {
  await http.delete(`suppliers/documents/${id}`)
}

export const fetchSupplierMediaProducts = async (
  _key: string,
  planId: string,
  params?: string,
) => {
  const searchParams = new URLSearchParams(params)
  const { data } = (await http
    .get(`media-plans/${planId}/channels`, { searchParams })
    .json()) as { data: SupplierProductOptionTuple[] }
  return data
}

export const fetchSupplierProducts = async (
  _key: string,
  id?: string,
  params?: string,
) => {
  const searchParams = new URLSearchParams(params)
  const { data } = (await http
    .get(`suppliers/${id}/products`, { searchParams })
    .json()) as { data: SupplierProduct[] }
  return data
}

type MediaOwenerSupplierAssociationPayload = {
  mediaOwnerId?: string
  supplierId: string | string[]
  supplierIds?: string | string[]
}
export const assocSupplierMediaOwener = async (
  pld: MediaOwenerSupplierAssociationPayload,
) => {
  const { mediaOwnerId, supplierId } = pld
  const supplierIds = Array.isArray(supplierId) ? supplierId : [supplierId]
  const json = { supplierIds }
  const res = await http
    .post(`suppliers/${mediaOwnerId}/connected-suppliers`, { json })
    .json()

  return res as unknown
}

export const dissocSupplierMediaOwener = async (
  pld: MediaOwenerSupplierAssociationPayload,
) => {
  const { mediaOwnerId, supplierId } = pld
  const _supplierId = Array.isArray(supplierId) ? supplierId[0] : supplierId
  const res = await http
    .delete(`suppliers/${mediaOwnerId}/connected-suppliers/${_supplierId}`)
    .json()

  return res as unknown
}

export const assocMediaOwnerSupplier = async (
  pld: MediaOwenerSupplierAssociationPayload,
) => {
  const { supplierIds, supplierId } = pld
  const _supplierIds = Array.isArray(supplierIds) ? supplierIds : [supplierIds]
  const json = { supplierIds: _supplierIds }
  const res = await http
    .post(`suppliers/${supplierId}/media-owners`, { json })
    .json()

  return res as unknown
}

export const dissocMediaOwnerSupplier = async (
  pld: MediaOwenerSupplierAssociationPayload,
) => {
  const { mediaOwnerId, supplierId } = pld
  const _mediaOwnerId = Array.isArray(mediaOwnerId)
    ? mediaOwnerId[0]
    : mediaOwnerId
  const res = await http
    .delete(`suppliers/${supplierId}/media-owners/${_mediaOwnerId}`)
    .json()

  return res as unknown
}

export const createVendorChannel = async (pld: {
  supplierId: string
  mediaTypeId: string
  name: string
  electronicOrderingActive: boolean
}) => {
  const res = await http.post(`channels`, { json: pld }).json()
  return res
}

export const updateVendorChannel = async (pld: {
  channelId: string
  name: string
  electronicOrderingActive: boolean
}) => {
  const { channelId, name, electronicOrderingActive } = pld
  const res = await http.patch(`channels/${channelId}`, {
    json: { name, electronicOrderingActive },
  })
  return res
}

export const deleteVendorChannel = async (id: string) =>
  await http.delete(`channels/${id}`)
